import type {
  CreateNotificationConfigurationADto,
  NotificationConfigurationADto,
} from '@cohort/admin-schemas/notification';
import {CreateNotificationConfigurationASchema} from '@cohort/admin-schemas/notification';
import {getNotificationIntegrations} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import CreateEditNotificationConfigurationModal from '@cohort/merchants/components/notifications/CreateEditNotificationConfigurationModal';
import {notificationConfigurationsKeys} from '@cohort/merchants/hooks/api/Notifications';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {notify} from '@cohort/merchants/hooks/toast';
import {createNotificationConfiguration} from '@cohort/merchants/lib/api/Notifications';
import type {EventId, NotificationIntegrationId} from '@cohort/shared/schema/apps';
import {COHORT_EMAIL_SUPPORTED_USER_EVENTS_SPECS_IDS} from '@cohort/shared/schema/apps/cohort/notifications/email';
import type {NotificationConfigurationResourceType} from '@cohort/shared/schema/common/notificationConfiguration';
import {PlusCircle} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

type NotificationSettingsListCreateBtnProps = {
  userEventId: EventId;
  notificationConfigurations: NotificationConfigurationADto[];
  resourceType: NotificationConfigurationResourceType;
  resourceId?: string;
};

const NotificationSettingsListCreateBtn: React.FC<NotificationSettingsListCreateBtnProps> = ({
  userEventId,
  notificationConfigurations,
  resourceType,
  resourceId,
}) => {
  const [showNotificationCreationModal, setShowNotificationCreationModal] = useState(false);
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();
  const notificationIntegrations = getNotificationIntegrations();
  const existingNotificationIntegrationIds: Array<NotificationIntegrationId> =
    notificationConfigurations
      .filter(nc => nc.resourceType === resourceType)
      .map(nc => nc.notificationIntegrationId);

  const {t} = useTranslation('pages', {
    keyPrefix: 'settings.notifications.notificationSettingsListCreateBtn',
  });

  const {
    isLoading: isCreatingNotificationConfiguration,
    mutate: createNotificationConfigurationMutation,
  } = useCohortMutation({
    mutationFn: async (data: CreateNotificationConfigurationADto) =>
      createNotificationConfiguration(merchant.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(notificationConfigurationsKeys.list(merchant.id));
    },
  });

  // Remove the unsupported user events for the "cohort.email" notification integration.
  const filteredNotificationIntegrations = notificationIntegrations.filter(
    notificationIntegration =>
      notificationIntegration.spec.id !== 'cohort.email' ||
      COHORT_EMAIL_SUPPORTED_USER_EVENTS_SPECS_IDS.includes(userEventId)
  );

  // Filter out the notificationIntegrations that are already configured.
  const unconfiguredNotificationIntegrations = filteredNotificationIntegrations.filter(
    notificationIntegration =>
      !existingNotificationIntegrationIds.includes(
        notificationIntegration.spec.id as NotificationIntegrationId
      )
  );

  // Hide the create btn if:
  // - we are on global settings page && there is already a notificationConfiguration for every notificationIntegration.
  // - we are on a resource settings page && there is already a custom notificationConfiguration for every notificationIntegration (cohort.email, cohort.custom-request...).
  const customNotificationConfigurations = notificationConfigurations.filter(
    notificationConfiguration => notificationConfiguration.resourceType === resourceType
  );

  const showCreateButton =
    resourceType === 'default'
      ? unconfiguredNotificationIntegrations.length > 0
      : filteredNotificationIntegrations.length > customNotificationConfigurations.length;

  return (
    <Fragment>
      {showCreateButton && (
        <Button variant="ghost" onClick={() => setShowNotificationCreationModal(true)}>
          <PlusCircle size={20} className="mr-2" /> {t('createNotificationButton')}
        </Button>
      )}

      {showNotificationCreationModal && (
        <CreateEditNotificationConfigurationModal
          isLoading={isCreatingNotificationConfiguration}
          userEventId={userEventId}
          onClose={() => setShowNotificationCreationModal(false)}
          existingNotificationIntegrationIds={existingNotificationIntegrationIds}
          resourceType={resourceType}
          onSubmit={rawData => {
            const parsedData = CreateNotificationConfigurationASchema.parse(rawData);
            createNotificationConfigurationMutation({...parsedData, resourceId});
            setShowNotificationCreationModal(false);
            notify('success', t('createNotificationSuccess'));
          }}
        />
      )}
    </Fragment>
  );
};

export default NotificationSettingsListCreateBtn;
